import { FunctionComponent, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as vp from 'vopay';
import { useForm, withStyles, Skeleton, MaterialSymbol, Tooltip } from 'vopay';

import Header from '../Header/Header';
import StepIndicator from '../StepIndicator/StepIndicator';
import { DetailsCard, Title, Button, DetailRow, MissingDetailRow } from 'UI';
import useFetchHubSpotDealDetails from '@/hooks/useApi/useFetchHubSpotDealDetails';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useHubspotFields, { HubspotFormValues } from './useHubspotFields';

import styles from './Hubspot.module.scss';

const cn = withStyles(styles);

// Sandbox reuses HubSpot deal IDs across test runs, so the "deal already exists" guard is
// disabled there. Defaults to enabled — only VITE_CHECK_HUBSPOT_DEAL_ID='false' turns it off.
const CHECK_DEAL_ID_EXISTS = import.meta.env.VITE_CHECK_HUBSPOT_DEAL_ID !== 'false';

export type HubspotData = {
  form: HubspotFormValues;
  details: vp.HubSpotDealDetails | null;
};

type HubspotProps = {
  steps: string[];
  currentStep: number;
  nextStep: () => void;
  onValuesChange: (values: HubspotData) => void;
};

const Hubspot: FunctionComponent<HubspotProps> = ({ steps, currentStep, nextStep, onValuesChange }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'R1AccountCreation.Hubspot' });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const [existingDealId, setExistingDealId] = useState<string | null>(null);
  const [, fetchDealDetails] = useFetchHubSpotDealDetails({});

  type Details = Awaited<ReturnType<typeof fetchDealDetails>>['Details'];
  const [details, setDetails] = useState<Details | null>(null);

  // Stable wrapper passed to fields — actual handler is defined after useForm
  const onDealIdBlurRef = useRef<(dealId: string) => void>(() => {});
  // Guards against a slow response for an earlier deal ID overwriting a newer lookup.
  const latestDealRequestRef = useRef(0);

  const fields = useHubspotFields(
    (dealId) => onDealIdBlurRef.current(dealId),
    details !== null,
    isLoading,
    existingDealId,
  );

  const { ctx, renderFormElements } = useForm<HubspotFormValues>({
    config: fields,
    initialValues: { hubSpotDealId: '', additionalNotes: '' },
    onSubmit: () => nextStep(),
  });

  const showError = async (message: string) => {
    await alertDialog.error(t('FetchError.title', { defaultValue: 'Failed to Fetch HubSpot Data' }), message, {
      confirmLabel: t('FetchError.ok', { defaultValue: 'OK' }),
    });
    ctx.setFieldValue('hubSpotDealId', '');
    setDetails(null);
    setExistingDealId(null);
  };

  const handleFetchDealDetails = async (dealId: string) => {
    const requestId = (latestDealRequestRef.current += 1);
    setIsLoading(true);
    try {
      const response = await fetchDealDetails(dealId);
      // A newer lookup superseded this one — drop the stale response.
      if (requestId !== latestDealRequestRef.current) return;
      if (CHECK_DEAL_ID_EXISTS && response.Success && response.DealIDExists) {
        setDetails(response.Details);
        setExistingDealId(dealId);
        await ctx.setFieldTouched('hubSpotDealId', true, true);
      } else if (response.Success) {
        setDetails(response.Details);
        setExistingDealId(null);
        toast.success(t('pullSuccess', { defaultValue: 'HubSpot data pulled successfully' }));
      } else {
        await showError(
          response.ErrorMessage ||
            t('FetchError.message', {
              defaultValue: 'An error occurred while fetching HubSpot deal details. Please try again.',
            }),
        );
      }
    } catch {
      if (requestId !== latestDealRequestRef.current) return;
      await showError(
        t('FetchError.message', {
          defaultValue: 'An error occurred while fetching HubSpot deal details. Please try again.',
        }),
      );
    } finally {
      // Only the latest request owns the loading state.
      if (requestId === latestDealRequestRef.current) setIsLoading(false);
    }
  };

  onDealIdBlurRef.current = handleFetchDealDetails;

  useEffect(() => {
    onValuesChange({ form: ctx.values, details: details ?? null });
  }, [ctx.values, details]);

  const hasAnyMissing =
    details !== null &&
    [
      details.CompanyProfile.CompanyHubspotID,
      details.CompanyProfile.Country,
      details.CompanyProfile.AccountLink,
      details.PrimaryContact.firstname,
      details.PrimaryContact.lastname,
      details.PrimaryContact.email,
      details.BusinessInformation.CompanyName,
      details.BusinessInformation.Dba,
      details.BusinessInformation.CompanyWebsite,
      details.BusinessInformation.SocialMediaPage,
      details.BusinessInformation.CompanyPhone,
      details.BusinessInformation.PaymentMethod,
    ].some((v) => !v);

  const row = (label: string, value: string | null | undefined) =>
    value ? (
      <DetailRow label={label} value={value} />
    ) : (
      <MissingDetailRow
        label={label}
        errorMessage={t('missingError', {
          defaultValue: 'This is a required field. Please update this in HubSpot and pull the data again.',
        })}
      />
    );

  const dealIdFields = fields.filter((f) => f.fieldName === 'hubSpotDealId');
  const additionalNotesFields = fields.filter((f) => f.fieldName === 'additionalNotes');

  return (
    <form className={cn('container')} onSubmit={ctx.handleSubmit}>
      <div className={cn('stickyHeader')}>
        <Header
          header={t('title', { defaultValue: 'Details' })}
          description={t('description', {
            defaultValue: 'Basic contact & business information for VoPay account creation.',
          })}
          rightArea={<StepIndicator steps={steps} activeStep={currentStep} />}
        />
      </div>

      <div className={cn('body')}>
        <div className={cn('leftCol')}>
          <DetailsCard className={cn('dealCard')}>
            {details === null && (
              <Title level="4">{t('Sections.CompanyProfile.title', { defaultValue: 'Company Profile' })}</Title>
            )}
            <div className={cn('dealGrid')}>{renderFormElements({ config: dealIdFields })}</div>
            {isLoading && (
              <>
                <div className={cn('skeletonRow')}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </div>
                <div className={cn('skeletonRow')}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </div>
              </>
            )}
          </DetailsCard>

          {isLoading && (
            <DetailsCard>
              <Skeleton height={22} width={138} />
              <div className={cn('skeletonRow')}>
                <Skeleton height={32} />
                <Skeleton height={32} />
              </div>
            </DetailsCard>
          )}

          {!isLoading && details !== null && (
            <>
              {/* Company Profile */}
              <DetailsCard>
                <div className={cn('sectionHeader')}>
                  <MaterialSymbol icon="business" size="42px" fillAmount={1} color="#2f82ff" />

                  <Title level="4">{t('Sections.CompanyProfile.title', { defaultValue: 'Company Profile' })}</Title>
                </div>
                {row(
                  t('Sections.CompanyProfile.hubSpotCompanyId', { defaultValue: 'HubSpot Company ID' }),
                  details.CompanyProfile.CompanyHubspotID,
                )}
                {row(t('Sections.CompanyProfile.country', { defaultValue: 'Country' }), details.CompanyProfile.Country)}
                {details.CompanyProfile.AccountLink ? (
                  <DetailRow
                    label={t('Sections.CompanyProfile.accountLink', { defaultValue: 'HubSpot Account Link' })}
                    value={
                      <a href={details.CompanyProfile.AccountLink} target="_blank" rel="noreferrer">
                        {details.CompanyProfile.AccountLink}
                      </a>
                    }
                  />
                ) : (
                  <MissingDetailRow
                    label={t('Sections.CompanyProfile.accountLink', { defaultValue: 'HubSpot Account Link' })}
                    errorMessage={t('missingError', {
                      defaultValue: 'This is a required field. Please update this in HubSpot and pull the data again.',
                    })}
                  />
                )}
              </DetailsCard>

              {/* Primary Contact Information */}
              <DetailsCard>
                <div className={cn('sectionHeader')}>
                  <MaterialSymbol icon="person" size="42px" fillAmount={1} color="#2f82ff" family="rounded" />

                  <Title level="4">
                    {t('Sections.PrimaryContact.title', { defaultValue: 'Primary Contact Information' })}
                  </Title>
                </div>
                {row(
                  t('Sections.PrimaryContact.firstName', { defaultValue: 'First Name' }),
                  details.PrimaryContact.firstname,
                )}
                {row(
                  t('Sections.PrimaryContact.lastName', { defaultValue: 'Last Name' }),
                  details.PrimaryContact.lastname,
                )}
                {row(
                  t('Sections.PrimaryContact.email', { defaultValue: 'Email Address' }),
                  details.PrimaryContact.email,
                )}
              </DetailsCard>

              {/* Business Information */}
              <DetailsCard>
                <div className={cn('sectionHeader')}>
                  <MaterialSymbol icon="storefront" size="42px" fillAmount={1} color="#2f82ff" family="rounded" />

                  <Title level="4">{t('Sections.BusinessInfo.title', { defaultValue: 'Business Information' })}</Title>
                </div>
                {row(
                  t('Sections.BusinessInfo.legalName', { defaultValue: 'Legal Business Name' }),
                  details.BusinessInformation.CompanyName,
                )}
                {row(
                  t('Sections.BusinessInfo.dba', { defaultValue: 'Doing-Business-As (DBA) Name' }),
                  details.BusinessInformation.Dba,
                )}
                {details.BusinessInformation.CompanyWebsite ? (
                  <DetailRow
                    label={t('Sections.BusinessInfo.website', { defaultValue: 'Company Website' })}
                    value={
                      <a href={details.BusinessInformation.CompanyWebsite} target="_blank" rel="noreferrer">
                        {details.BusinessInformation.CompanyWebsite}
                      </a>
                    }
                  />
                ) : (
                  <MissingDetailRow
                    label={t('Sections.BusinessInfo.website', { defaultValue: 'Company Website' })}
                    errorMessage={t('missingError', {
                      defaultValue: 'This is a required field. Please update this in HubSpot and pull the data again.',
                    })}
                  />
                )}
                {details.BusinessInformation.SocialMediaPage ? (
                  <DetailRow
                    label={t('Sections.BusinessInfo.socialMedia', { defaultValue: 'Business Social Media' })}
                    value={
                      <a href={details.BusinessInformation.SocialMediaPage} target="_blank" rel="noreferrer">
                        {details.BusinessInformation.SocialMediaPage}
                      </a>
                    }
                  />
                ) : (
                  <MissingDetailRow
                    label={t('Sections.BusinessInfo.socialMedia', { defaultValue: 'Business Social Media' })}
                    errorMessage={t('missingError', {
                      defaultValue: 'This is a required field. Please update this in HubSpot and pull the data again.',
                    })}
                  />
                )}
                {row(
                  t('Sections.BusinessInfo.phone', { defaultValue: 'Business Phone Number' }),
                  details.BusinessInformation.CompanyPhone,
                )}
              </DetailsCard>

              {/* Payment Information */}
              <DetailsCard>
                <div className={cn('sectionHeader')}>
                  <MaterialSymbol icon="account_balance" size="42px" fillAmount={1} color="#2f82ff" />

                  <Title level="4">{t('Sections.PaymentInfo.title', { defaultValue: 'Payment Information' })}</Title>
                </div>
                {details.BusinessInformation.PaymentMethod ? (
                  <DetailRow
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {t('Sections.PaymentInfo.paymentMethod', { defaultValue: 'Payment Method' })}
                        <Tooltip
                          information={t('Sections.PaymentInfo.paymentMethodTooltip', {
                            defaultValue:
                              'This payment method will be used for licensing fee & implementation fee if applicable.',
                          })}
                          type="hover"
                          variant="outlined"
                          icon="info"
                          position="top"
                          buttonWidth="18px"
                          iconWidth="12"
                          hasArrow
                        />
                      </span>
                    }
                    value={details.BusinessInformation.PaymentMethod}
                  />
                ) : (
                  <MissingDetailRow
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {t('Sections.PaymentInfo.paymentMethod', { defaultValue: 'Payment Method' })}
                        <Tooltip
                          information={t('Sections.PaymentInfo.paymentMethodTooltip', {
                            defaultValue:
                              'This payment method will be used for licensing fee & implementation fee if applicable.',
                          })}
                          type="hover"
                          variant="outlined"
                          icon="info"
                          position="top"
                          buttonWidth="18px"
                          iconWidth="12"
                          hasArrow
                        />
                      </span>
                    }
                    errorMessage={t('missingError', {
                      defaultValue: 'This is a required field. Please update this in HubSpot and pull the data again.',
                    })}
                  />
                )}
              </DetailsCard>
            </>
          )}
        </div>

        {details !== null && (
          <DetailsCard className={cn('notesCard')}>{renderFormElements({ config: additionalNotesFields })}</DetailsCard>
        )}
      </div>

      <div className={cn('footer')}>
        <div className={cn('footerLeft')}>
          {details !== null && (
            <div>
              <Button
                type="button"
                iconLeftProps={{
                  icon: 'cloud_download',
                  fillAmount: 1,
                }}
                onClick={() => void handleFetchDealDetails(ctx.values.hubSpotDealId)}
                disabled={isLoading}
                size="small"
              >
                {t('pullHubSpotData', { defaultValue: 'Pull HubSpot Data' })}
              </Button>

              <p className={cn('pullSubtext')}>
                {t('pullSubtext', {
                  defaultValue: 'Made changes to the company profile on HubSpot? Fetch the data again.',
                })}
              </p>
            </div>
          )}
        </div>
        <Button
          colorScheme="primary"
          disabled={!ctx.isValid || isLoading || details === null || hasAnyMissing}
          type="submit"
        >
          {t('next', { defaultValue: 'Next' })}
        </Button>
      </div>
    </form>
  );
};

export default Hubspot;
